/** @format */

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { get, map as lmap, merge, pick } from 'lodash-es';
import { ModelMapper } from 'model-mapper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHeaderSearchFilter, IHeaderSearch } from '../classes/header-search';
import { Quotation, QuotationStatus } from '../classes/quotation';
import { LibConfig, LibConfigService } from '../config.service';

export interface IQuotationFilter extends IHeaderSearch {
  year?: number;
  realEstateStructureId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class QuotationService {
  constructor(@Inject(LibConfigService) protected config: LibConfig, protected http: HttpClient) {}

  public get(id: string): Observable<Quotation> {
    return this.http
      .get<any[]>(`${this.config.environment.apiUrl}/quotations/${id}`)
      .pipe(map(data => new ModelMapper(Quotation).map(data)));
  }

  public delete(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.config.environment.apiUrl}/quotations/${id}`);
  }

  public create(data: any): Observable<Quotation> {
    return this.http
      .post<any>(`${this.config.environment.apiUrl}/quotations`, this.serializeForDto(data))
      .pipe(map(res => new ModelMapper(Quotation).map(res)));
  }

  public update(id: string, data: any): Observable<boolean> {
    return this.http.patch<boolean>(`${this.config.environment.apiUrl}/quotations/${id}`, this.serializeForDto(data));
  }

  public setStatus(id: string, status: QuotationStatus, canceledReason?: string): Observable<boolean> {
    return this.http.patch<boolean>(`${this.config.environment.apiUrl}/quotations/${id}/set-status`, {
      status,
      canceledReason,
    });
  }

  protected buildSearchFilter(search: IQuotationFilter): any {
    const filter: any = buildHeaderSearchFilter(search);
    if (search?.year) filter.year = search.year;
    if (search?.realEstateStructureId) filter.realEstateStructureId = search.realEstateStructureId;
    return filter;
  }

  private serializeForDto(data: any): any {
    return merge(
      pick(
        data,
        'externalReference',
        'purchaseOrderNumber',
        'status',
        'contractor.id',
        'organizationalUnit.id',
        'realEstateStructure.id',
        'file',
        'files',
        'signatory',
        'comment',
        'canceledReason'
      ),
      {
        inspections: lmap(get(data, 'inspections'), d => pick(d, 'id')),
        interventions: lmap(get(data, 'interventions'), d => pick(d, 'id')),
      }
    );
  }
}
